import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import { getLinks, deleteLink, bulkDeleteLinks, markLinkAsDownloaded, bulkMarkDownloaded, extractErrorMessage } from '../services/api';
import { FaSearch, FaTrash, FaExternalLinkAlt, FaFilter, FaDownload, FaCheck } from 'react-icons/fa';
import { useToast } from '../components/Toast';
import '../styles/Links.css';

function Links({ user }) {
  const toast = useToast();
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [selectedLinks, setSelectedLinks] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, limit: 50, offset: 0 });

  useEffect(() => {
    fetchLinks();
  }, [category, pagination.offset]);

  const fetchLinks = async () => {
    try {
      setLoading(true);
      const data = await getLinks({
        category: category || undefined,
        search: search || undefined,
        limit: pagination.limit,
        offset: pagination.offset
      });
      setLinks(data.links);
      setPagination({ ...pagination, total: data.pagination.total });
    } catch (error) {
      console.error('Error fetching links:', error);
      toast.error(extractErrorMessage(error, 'Links konnten nicht geladen werden'));
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchLinks();
  };

  const handleDelete = async (id) => {
    if (saving) return;
    if (!window.confirm('Are you sure you want to delete this link?')) return;
    setSaving(true);
    try {
      await deleteLink(id);
      toast.success('Link gelöscht');
      fetchLinks();
    } catch (error) {
      console.error('Error deleting link:', error);
      toast.error(extractErrorMessage(error, 'Fehler beim Löschen des Links'));
    } finally {
      setSaving(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedLinks.length === 0) return;
    if (saving) return;
    if (!window.confirm(`Delete ${selectedLinks.length} selected link(s)?`)) return;
    setSaving(true);
    try {
      await bulkDeleteLinks(selectedLinks);
      setSelectedLinks([]);
      toast.success('Links gelöscht');
      fetchLinks();
    } catch (error) {
      console.error('Error bulk deleting links:', error);
      toast.error(extractErrorMessage(error, 'Fehler beim Löschen der Links'));
    } finally {
      setSaving(false);
    }
  };

  const toggleSelectLink = (id) => {
    setSelectedLinks(prev =>
      prev.includes(id) ? prev.filter(lid => lid !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedLinks.length === links.length) {
      setSelectedLinks([]);
    } else {
      setSelectedLinks(links.map(link => link.id));
    }
  };

  const handleMarkDownloaded = async (id, currentStatus) => {
    if (saving) return;
    setSaving(true);
    try {
      await markLinkAsDownloaded(id, !currentStatus);
      fetchLinks();
    } catch (error) {
      console.error('Error marking link:', error);
      toast.error(extractErrorMessage(error, 'Download-Status konnte nicht aktualisiert werden'));
    } finally {
      setSaving(false);
    }
  };

  const handleBulkMarkDownloaded = async (downloaded) => {
    if (selectedLinks.length === 0) return;
    if (saving) return;
    setSaving(true);
    try {
      await bulkMarkDownloaded(selectedLinks, downloaded);
      setSelectedLinks([]);
      toast.success('Download-Status aktualisiert');
      fetchLinks();
    } catch (error) {
      console.error('Error bulk marking links:', error);
      toast.error(extractErrorMessage(error, 'Download-Status konnte nicht aktualisiert werden'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="app-layout">
      <Navbar user={user} />
      <main className="main-content">
        <div className="page-header">
          <h1>Links</h1>
          {selectedLinks.length > 0 && (
            <div className="bulk-actions">
              <button onClick={() => handleBulkMarkDownloaded(true)} className="bulk-action-btn downloaded">
                <FaCheck /> Mark {selectedLinks.length} as Downloaded
              </button>
              <button onClick={() => handleBulkMarkDownloaded(false)} className="bulk-action-btn not-downloaded">
                <FaDownload /> Mark {selectedLinks.length} as Not Downloaded
              </button>
              {user.isAdmin && (
                <button onClick={handleBulkDelete} className="bulk-action-btn delete">
                  <FaTrash /> Delete {selectedLinks.length}
                </button>
              )}
            </div>
          )}
        </div>

        <div className="links-filters">
          <form onSubmit={handleSearch} className="search-form">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search links..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <button type="submit">Search</button>
          </form>

          <div className="category-filter">
            <FaFilter />
            <select value={category} onChange={(e) => setCategory(e.target.value)}>
              <option value="">All Categories</option>
              <option value="nova">💗 Nova</option>
              <option value="fayne">❤️ Fayne</option>
            </select>
          </div>
        </div>

        {loading ? (
          <div className="loading-container">
            <div className="spinner"></div>
          </div>
        ) : (
          <>
            <div className="links-table">
              <table>
                <thead>
                  <tr>
                    <th>
                      <input
                        type="checkbox"
                        checked={selectedLinks.length === links.length && links.length > 0}
                        onChange={toggleSelectAll}
                      />
                    </th>
                    <th>Status</th>
                    <th>Category</th>
                    <th>URL</th>
                    <th>Author</th>
                    <th>Channel</th>
                    <th>Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {links.length > 0 ? (
                    links.map((link) => (
                      <tr key={link.id} className={link.downloaded ? 'downloaded-row' : ''}>
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedLinks.includes(link.id)}
                            onChange={() => toggleSelectLink(link.id)}
                          />
                        </td>
                        <td>
                          <button
                            onClick={() => handleMarkDownloaded(link.id, link.downloaded)}
                            className={`download-status-btn ${link.downloaded ? 'downloaded' : 'not-downloaded'}`}
                            title={link.downloaded ? `Downloaded by ${link.downloaded_by} on ${new Date(link.downloaded_at).toLocaleString()}` : 'Mark as downloaded'}
                          >
                            {link.downloaded ? <FaCheck /> : <FaDownload />}
                          </button>
                        </td>
                        <td>
                          <span className={`category-badge ${link.category}`}>
                            {link.category === 'nova' ? '💗 Nova' : '❤️ Fayne'}
                          </span>
                        </td>
                        <td className="url-cell">
                          <a href={link.url} target="_blank" rel="noopener noreferrer">
                            {link.url.length > 50 ? link.url.substring(0, 50) + '...' : link.url}
                          </a>
                        </td>
                        <td>{link.author_name}</td>
                        <td>{link.channel_name || 'N/A'}</td>
                        <td>{new Date(link.timestamp).toLocaleDateString()}</td>
                        <td className="actions-cell">
                          <a href={link.url} target="_blank" rel="noopener noreferrer" className="action-btn">
                            <FaExternalLinkAlt />
                          </a>
                          {user.isAdmin && (
                            <button onClick={() => handleDelete(link.id)} className="action-btn delete">
                              <FaTrash />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="8" className="no-data">
                        No links found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="pagination">
              <button
                onClick={() => setPagination({ ...pagination, offset: Math.max(0, pagination.offset - pagination.limit) })}
                disabled={pagination.offset === 0}
              >
                Previous
              </button>
              <span>
                Showing {pagination.offset + 1} - {Math.min(pagination.offset + pagination.limit, pagination.total)} of {pagination.total}
              </span>
              <button
                onClick={() => setPagination({ ...pagination, offset: pagination.offset + pagination.limit })}
                disabled={pagination.offset + pagination.limit >= pagination.total}
              >
                Next
              </button>
            </div>
          </>
        )}
      </main>
    </div>
  );
}

export default Links;
